import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { useAuth } from "../AuthContext";
import {
  CREATE_CUSTOMER_MUTATION,
  CREATE_ADDRESS_MUTATION,
} from "../graphql/customers";
import CloseIcon from "@mui/icons-material/Close";

interface CustomerDialogProps {
  onClose: () => void;
  onCreated?: () => void;
}

const dialogOverlayStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
};

const dialogStyle: React.CSSProperties = {
  backgroundColor: "white",
  borderRadius: "8px",
  boxShadow: "0 20px 25px rgba(0, 0, 0, 0.15)",
  maxWidth: "600px",
  width: "90%",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
};

const dialogHeaderStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "24px",
  borderBottom: "1px solid #e5e7eb",
};

const closeButtonStyle: React.CSSProperties = {
  background: "none",
  border: "none",
  fontSize: "24px",
  cursor: "pointer",
  color: "#6b7280",
};

const dialogContentStyle: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: "24px",
};

const dialogFooterStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px",
  padding: "16px 24px",
  borderTop: "1px solid #e5e7eb",
};

const sectionStyle: React.CSSProperties = {
  marginBottom: "24px",
};

const sectionTitleStyle: React.CSSProperties = {
  fontSize: "14px",
  fontWeight: 600,
  color: "#374151",
  marginBottom: "12px",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
};

const formGroupStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "6px",
  marginBottom: "16px",
};

const labelStyle: React.CSSProperties = {
  fontSize: "13px",
  fontWeight: 600,
  color: "#374151",
};

const inputStyle: React.CSSProperties = {
  padding: "9px 12px",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  fontSize: "14px",
  fontFamily: "inherit",
};

const textareaStyle: React.CSSProperties = {
  padding: "9px 12px",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  fontSize: "14px",
  fontFamily: "inherit",
  resize: "vertical",
  minHeight: "80px",
};

const submitButtonStyle: React.CSSProperties = {
  padding: "10px 24px",
  backgroundColor: "#10b981",
  color: "white",
  border: "none",
  borderRadius: "6px",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
};

const cancelButtonStyle: React.CSSProperties = {
  padding: "10px 24px",
  backgroundColor: "#f3f4f6",
  color: "#374151",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  fontSize: "14px",
  fontWeight: 600,
  cursor: "pointer",
};

const errorStyle: React.CSSProperties = {
  padding: "12px",
  backgroundColor: "#fee2e2",
  color: "#dc2626",
  borderRadius: "6px",
  fontSize: "13px",
  marginBottom: "16px",
};

export default function CustomerDialog({ onClose, onCreated }: CustomerDialogProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    street: "",
    city: "",
    state: "",
    zip: "",
    addressType: "primary",
  });

  const [createCustomer] = useMutation(CREATE_CUSTOMER_MUTATION);
  const [createAddress] = useMutation(CREATE_ADDRESS_MUTATION);

  const hasAddressFields = () => {
    return (
      formData.street.trim() &&
      formData.city.trim() &&
      formData.state.trim() &&
      formData.zip.trim()
    );
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      setError("Customer name is required");
      return;
    }

    if (!user?.domain_id) {
      setError("Domain ID not found");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const customerResult = await createCustomer({
        variables: {
          name: formData.name.trim(),
          email: formData.email.trim() || null,
          phone: formData.phone.trim() || null,
          domainId: user.domain_id,
        },
      });

      const customerId = customerResult.data?.createCustomer?.customer?.id;

      if (customerId && hasAddressFields()) {
        await createAddress({
          variables: {
            customerId,
            domainId: user.domain_id,
            street: formData.street.trim(),
            city: formData.city.trim(),
            state: formData.state.trim().toUpperCase(),
            zip: formData.zip.trim(),
            addressType: formData.addressType,
          },
        });
      }

      onCreated?.();
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to create customer");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <div style={dialogOverlayStyle}>
      <div style={dialogStyle}>
        <div style={dialogHeaderStyle}>
          <h2
            style={{
              margin: 0,
              fontSize: "20px",
              color: "#111827",
              fontWeight: 700,
            }}
          >
            New Customer
          </h2>
          <button style={closeButtonStyle} onClick={onClose} title="Close">
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div style={dialogContentStyle}>
          {error && <div style={errorStyle}>{error}</div>}

          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Customer Information</div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>
                Name <span style={{ color: "#dc2626" }}>*</span>
              </label>
              <input
                type="text"
                style={inputStyle}
                placeholder="e.g., Acme Corporation"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                onKeyPress={handleKeyPress}
                disabled={loading}
                autoFocus
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Email (Optional)</label>
              <input
                type="email"
                style={inputStyle}
                placeholder="e.g., contact@acme.com"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Phone (Optional)</label>
              <input
                type="tel"
                style={inputStyle}
                placeholder="e.g., (555) 123-4567"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
            </div>
          </div>

          <div style={sectionStyle}>
            <div style={sectionTitleStyle}>Address (Optional)</div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Street</label>
              <input
                type="text"
                style={inputStyle}
                placeholder="e.g., 123 Main St"
                value={formData.street}
                onChange={(e) =>
                  setFormData({ ...formData, street: e.target.value })
                }
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>City</label>
              <input
                type="text"
                style={inputStyle}
                placeholder="e.g., Springfield"
                value={formData.city}
                onChange={(e) =>
                  setFormData({ ...formData, city: e.target.value })
                }
                onKeyPress={handleKeyPress}
                disabled={loading}
              />
            </div>

            <div style={{ display: "flex", gap: "12px", marginBottom: "16px" }}>
              <div style={{ flex: 1, ...formGroupStyle }}>
                <label style={labelStyle}>State</label>
                <input
                  type="text"
                  style={inputStyle}
                  placeholder="e.g., IL"
                  value={formData.state}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      state: e.target.value.toUpperCase().slice(0, 2),
                    })
                  }
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                  maxLength={2}
                />
              </div>
              <div style={{ flex: 1, ...formGroupStyle }}>
                <label style={labelStyle}>ZIP</label>
                <input
                  type="text"
                  style={inputStyle}
                  placeholder="e.g., 62701"
                  value={formData.zip}
                  onChange={(e) =>
                    setFormData({ ...formData, zip: e.target.value })
                  }
                  onKeyPress={handleKeyPress}
                  disabled={loading}
                />
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle}>Address Type</label>
              <select
                style={inputStyle}
                value={formData.addressType}
                onChange={(e) =>
                  setFormData({ ...formData, addressType: e.target.value })
                }
                disabled={loading}
              >
                <option value="primary">Primary</option>
                <option value="billing">Billing</option>
                <option value="shipping">Shipping</option>
              </select>
            </div>
          </div>
        </div>

        <div style={dialogFooterStyle}>
          <button
            style={cancelButtonStyle}
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            style={submitButtonStyle}
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Saving..." : "Create Customer"}
          </button>
        </div>
      </div>
    </div>
  );
}
